import type { PointerEvent as ReactPointerEvent } from 'react'
import { create } from 'zustand'
import { useGameState } from './useGameState'

/**
 * Pointer-based drag and drop for tiles. Unlike HTML drag and drop it starts as
 * soon as the finger or mouse moves (no long press on phones) and never scrolls
 * the page, because draggable tiles set touch-action: none. A press that
 * doesn't move stays a tap, so tap-to-select keeps working.
 */

type Source = { rackIndex: number } | { row: number; col: number }

interface DragState {
  /** The tile being dragged, or null. Its position isn't state: see moveGhost. */
  ghost: { letter: string; from: string } | null
  /** The empty square under the pointer, as "row,col". */
  over: string | null
  /** The rack position under the pointer, when the tile would move there. */
  overRack: number | null
}

export const useDrag = create<DragState>(() => ({ ghost: null, over: null, overRack: null }))

/**
 * The floating tile follows the pointer by setting its transform directly,
 * once per frame, rather than through React state: re-rendering on every
 * pointer move made it lag behind the finger and stutter.
 */
let ghostEl: HTMLElement | null = null
let pointer = { x: 0, y: 0 }

function placeGhost() {
  // Above the finger, so the square underneath stays visible.
  if (ghostEl) ghostEl.style.transform = `translate3d(${pointer.x}px, ${pointer.y}px, 0) translate(-50%, -85%) scale(1.15)`
}

/** DragGhost registers its element here; it is positioned as soon as it mounts. */
export function ghostRef(el: HTMLElement | null) {
  ghostEl = el
  placeGhost()
}

/** How far a press must move before it becomes a drag rather than a tap. */
const THRESHOLD = 6

/**
 * Squares that accept a tile carry data-cell="row,col"; the rack is the "Your
 * letters" listbox, and each of its positions carries data-rack-pos.
 */
function targetAt(x: number, y: number): { cell: string | null; rack: boolean; rackPos: number | null } {
  const el = document.elementFromPoint(x, y)
  const pos = el?.closest<HTMLElement>('[data-rack-pos]')?.dataset.rackPos
  return {
    cell: el?.closest<HTMLElement>('[data-cell]')?.dataset.cell ?? null,
    rack: Boolean(el?.closest('[aria-label="Your letters"]')),
    rackPos: pos === undefined ? null : Number(pos),
  }
}

export function startTileDrag(e: ReactPointerEvent<HTMLElement>, source: Source, letter: string) {
  if (e.button !== 0 || useGameState.getState().record?.finished) return
  const startX = e.clientX
  const startY = e.clientY
  const pointerId = e.pointerId
  let dragging = false
  let frame = 0

  // At most once per frame: move the tile and, only if they changed, the highlighted square or rack position.
  const update = () => {
    frame = 0
    placeGhost()
    const target = targetAt(pointer.x, pointer.y)
    const over = target.cell
    const overRack = target.rackPos
    const now = useDrag.getState()
    if (over !== now.over || overRack !== now.overRack) useDrag.setState({ over, overRack })
  }

  const move = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return
    if (!dragging && Math.hypot(ev.clientX - startX, ev.clientY - startY) < THRESHOLD) return
    ev.preventDefault()
    pointer = { x: ev.clientX, y: ev.clientY }
    if (!dragging) {
      dragging = true
      useDrag.setState({ ghost: { letter, from: sourceKey(source) } })
    }
    frame ||= requestAnimationFrame(update)
  }

  const end = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', end)
    window.removeEventListener('pointercancel', end)
    cancelAnimationFrame(frame)
    useDrag.setState({ ghost: null, over: null, overRack: null })
    if (!dragging || ev.type === 'pointercancel') return

    // A drag is not a tap: swallow the click the browser may send right after
    // pointerup (only when it ends on the element it started on), then stop.
    window.addEventListener('click', swallow, { capture: true })
    setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0)

    const { cell, rack, rackPos } = targetAt(ev.clientX, ev.clientY)
    const game = useGameState.getState()
    if (cell) {
      const [row, col] = cell.split(',').map(Number)
      game.dropTile(source, { row, col })
      return
    }
    if (!rack) return
    // On the rack: a board tile goes back, and either kind moves to the position it was dropped on.
    const rackIndex =
      'rackIndex' in source ? source.rackIndex : game.placed.find((p) => p.row === source.row && p.col === source.col)?.rackIndex
    if ('row' in source) game.dropTile(source, null)
    if (rackIndex !== undefined && rackPos !== null) game.moveInRack(rackIndex, rackPos)
  }

  window.addEventListener('pointermove', move, { passive: false })
  window.addEventListener('pointerup', end)
  window.addEventListener('pointercancel', end)
}

/** Identifies a drag source, so the tile being dragged can be shown as lifted. */
export function sourceKey(source: Source): string {
  return 'rackIndex' in source ? `rack:${source.rackIndex}` : `cell:${source.row},${source.col}`
}

function swallow(e: Event) {
  e.stopPropagation()
}
